import { featureMountPathBuilder } from '../utils';
import { getCurrentFeatureCode, OktaUserInfo } from '../auth';
import { useAppConfigContext } from '../contexts';
import QuestIcon from '@/assets/img/quest.svg';
import ArchivesIcon from '@/assets/img/archives.svg';
import DefaultIcon from '@/assets/img/default.svg';
import { adminFeature } from '@/app/features/admin/admin-module-feature';
import { useCallback, useMemo } from 'react';
import {
  AiOutlineAppstore,
  AiOutlineTool,
  AiOutlineUser,
} from 'react-icons/ai/index.js';
import { useHistory } from 'react-router';
import { useAsync } from '@ssar-app/okta-authn-react';
import {
  Feature,
  FeatureCodes,
  getListOfSupportedFeatures,
} from '@ssar-app/domain';
import {
  Box,
  Menu,
  MenuButton,
  Icon,
  MenuList,
  MenuItem,
  Image,
} from '@chakra-ui/react';
import { IconType } from 'react-icons/lib/cjs/index.js';

interface FeatureSelectorProps {
  userFeatures: Feature[];
}

const iconsMap: Record<string, string | IconType> = {
  [FeatureCodes.AscendQuest]: QuestIcon,
  [FeatureCodes.AscendArchives]: ArchivesIcon,
  [FeatureCodes.Admin]: AiOutlineTool,
  [FeatureCodes.UserAccount]: AiOutlineUser,
};
const FeatureSelectorInternal = (props: FeatureSelectorProps) => {
  const { userFeatures } = props;

  const featureCode = getCurrentFeatureCode();
  const history = useHistory();

  const featuresWithIcons = useMemo(() => {
    const userProfile = OktaUserInfo.getOktaUserInfo();
    const products = getListOfSupportedFeatures(
      userProfile,
      userFeatures,
      adminFeature.code,
    );

    return products.map((feature) => {
      return {
        icon: iconsMap[feature.code] || DefaultIcon,
        ...feature,
      };
    });
  }, [userFeatures]);

  const setFeature = useCallback(
    (feature: Feature) => {
      const path = featureMountPathBuilder(feature);
      setTimeout(() => {
        history.push(path);
      });
    },
    [history],
  );

  return (
    <Box>
      {featuresWithIcons && featuresWithIcons.length > 1 && (
        <Menu placement='bottom-end'>
          <MenuButton bg='white'>
            <Icon
              as={AiOutlineAppstore}
              color='gray.700'
              h={9}
              w={9}
              mr={4}
              verticalAlign='middle'
            />
          </MenuButton>
          <MenuList zIndex={9999}>
            {featuresWithIcons?.map((feature) => (
              <MenuItem
                h={10}
                key={feature.code}
                {...(feature.code === featureCode
                  ? {
                      bg: 'var(--chakra-colors-primary) !important',
                      color: 'white',
                    }
                  : {
                      _hover: { color: 'primary' },
                      onClick: () => setFeature(feature),
                    })}
              >
                {typeof feature.icon === 'string' ? (
                  <Image src={feature.icon} alt={feature.name} mr={2} />
                ) : (
                  <Icon as={feature.icon} mr={2} fontSize='lg' h={4} w={6} />
                )}
                {feature?.name}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      )}
    </Box>
  );
};

// TODO: (CV) This is where we would get the applicable features from backend/some service that would filter the features by user profile/permissions/authz etc at backend and fetch it.
const useUserFeatures = () => {
  const { features } = useAppConfigContext();
  const fn = useCallback(() => {
    return Promise.resolve(features);
  }, [features]);
  return useAsync(fn, true);
};

export const FeatureSelector = () => {
  const { status, value, error } = useUserFeatures();
  if (status !== 'completed') {
    return null;
  } else if (error) {
    console.error(error);
    return null;
  } else if (value) {
    return (
      <FeatureSelectorInternal userFeatures={value}></FeatureSelectorInternal>
    );
  } else {
    return null;
  }
};
